import { API_BASE } from './constants';

export function formatApiError(data: Record<string, unknown>): string {
    const msg = typeof data.message === 'string' ? data.message : '';
    const altErr = typeof data.error === 'string' ? data.error : '';
    const errObj = data.errors;
    if (errObj && typeof errObj === 'object') {
        const parts = Object.values(errObj as Record<string, unknown>)
            .flatMap((v) => (Array.isArray(v) ? v : []))
            .filter((x): x is string => typeof x === 'string');
        if (parts.length > 0) {
            // Standardni Laravel 422 vraca ISTU poruku i u "message" i u "errors" kad
            // pukne samo jedno polje ("The oib field format is invalid." oba mjesta).
            // Raniji kod je spajao message+errors pa je korisnik vidio poruku dvaput
            // (Irenina prijava, t.4). "errors" je uvijek detaljniji izvor - dovoljan je sam.
            return parts.join(' ');
        }
    }

    return msg || altErr || 'API error';
}

/**
 * Greska s ocuvanim tijelom odgovora. Standardni throw new Error(poruka) bi bacio SAMO tekst
 * i odbacio ostatak JSON-a (npr. KYC "readiness" checklistu koju backend salje uz 422 na
 * /kyc/cases/{id}/submit) - pozivatelj bi onda mogao prikazati samo genericku recenicu
 * ("KYC dosje nije potpun.") bez popisa sto tocno nedostaje. .body cuva puni JSON za one
 * pozivatelje kojima ta struktura treba.
 */
export class ApiError extends Error {
    body: Record<string, unknown>;
    status: number;
    constructor(message: string, status: number, body: Record<string, unknown>) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

export async function api<T>(path: string, token: string | null, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(options.headers ?? {}),
        },
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new ApiError(formatApiError(data as Record<string, unknown>), response.status, data as Record<string, unknown>);
    }
    return data as T;
}

/**
 * JSON mutation helper. Adds X-XSRF-TOKEN automatically when present so Sanctum SPA flows work.
 */
export async function mutate<T>(
    path: string,
    token: string | null,
    method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    body?: Record<string, unknown>,
): Promise<T> {
    return api<T>(path, token, {
        method,
        body: body ? JSON.stringify(body) : undefined,
    });
}

/** Download arbitrary file (xlsx, pdf, csv) sa Authorization headerom. Trigger browser save. */
export async function downloadFile(path: string, token: string | null, filename: string): Promise<void> {
    const response = await fetch(`${API_BASE}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
        throw new Error(`Download fail: ${response.status}`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

/**
 * Dohvati datoteku s auth headerom i vrati blob: URL za prikaz UNUTAR stranice (iframe, img).
 * Pozivatelj je duzan pozvati URL.revokeObjectURL kad prikaz makne.
 * mime se predaje jer download ruta salje Content-Disposition: attachment, pa bi ga preglednik
 * spustio umjesto prikazao; blob se zato pretipka u stvarni mime zapisa.
 */
export async function fetchBlobUrl(path: string, token: string | null, mime?: string | null): Promise<string> {
    const response = await fetch(`${API_BASE}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
        throw new Error(`Preview fail: ${response.status}`);
    }
    const blob = await response.blob();
    const typed = mime && blob.type !== mime ? new Blob([blob], { type: mime }) : blob;

    return URL.createObjectURL(typed);
}

/** Dohvati datoteku s auth headerom i otvori je INLINE u novom tabu (blob: URL), kao m.31 viewer. */
export async function openFileInline(path: string, token: string | null): Promise<void> {
    const response = await fetch(`${API_BASE}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
        throw new Error(`Open fail: ${response.status}`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    // blob ostaje živ dok je tab otvoren; oslobodi nakon kratke odgode
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/**
 * Multipart upload s method-spoofingom (POST + _method) — da Laravel route-a na PATCH/PUT akciju,
 * a PHP i dalje parsira file (multipart PATCH ne radi nativno). Koristi se za upload priloga pri uređivanju.
 */
export async function uploadFileMethod<T>(path: string, token: string | null, formData: FormData, method: 'PATCH' | 'PUT' = 'PATCH'): Promise<T> {
    formData.append('_method', method);
    return uploadFile<T>(path, token, formData);
}

/** Multipart upload (npr. Monestiq privitci). Ne postavlja Content-Type — browser dodaje boundary. */
export async function uploadFile<T>(path: string, token: string | null, formData: FormData): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers,
        body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(formatApiError(data as Record<string, unknown>));
    }
    return data as T;
}
